// Loans & Advances ledger for the HR module of Khyati Portal / RSR International.
// Tracks employee loans / salary advances with a running-balance transaction ledger.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
  HRC, money, fmtDate, Card, SectionTitle, SubTabs, Th, Td, Table,
  Pill, Btn, Stat, Field, input, Modal, Empty, Loading,
} from './hrkit';

const today = () => new Date().toISOString().slice(0, 10);
const num = (v) => Number(v || 0);

const TYPE_TABS = [
  { key: 'all', label: 'All', value: null },
  { key: 'loan', label: 'Loans', value: 'loan' },
  { key: 'advance', label: 'Advances', value: 'advance' },
];
const STATUS_TABS = [
  { key: 'all', label: 'All', value: null },
  { key: 'active', label: 'Active', value: 'active' },
  { key: 'pending', label: 'Pending', value: 'pending' },
  { key: 'closed', label: 'Closed', value: 'closed' },
];

// Coloured chip for loan vs advance.
const TypeChip = ({ value }) => {
  const isAdvance = value === 'advance';
  const c = isAdvance ? HRC.amber : HRC.navy;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      color: c, background: c + '1A', textTransform: 'capitalize', whiteSpace: 'nowrap',
    }}>{isAdvance ? 'Advance' : 'Loan'}</span>
  );
};

// Tiny installment progress bar.
const Progress = ({ paid, total }) => {
  const t = Math.max(0, num(total));
  const p = Math.max(0, Math.min(num(paid), t || num(paid)));
  const pct = t > 0 ? Math.round((p / t) * 100) : 0;
  return (
    <div style={{ minWidth: 110 }}>
      <div style={{ fontSize: 11, color: HRC.muted, fontWeight: 600, marginBottom: 3 }}>
        {p}/{t || '—'} {t ? `(${pct}%)` : ''}
      </div>
      <div style={{ height: 6, background: HRC.border, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? HRC.green : HRC.navy, borderRadius: 4 }} />
      </div>
    </div>
  );
};

const txnColor = (t) => (t === 'repayment' ? HRC.green : t === 'adjustment' ? HRC.amber : HRC.red);

// ───────────────────────── Detail / Ledger modal ─────────────────────────
function LedgerModal({ loanId, onClose, onChange }) {
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ amount: '', txn_date: today(), note: '' });

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    api.get(`/hr/loans/${loanId}`)
      .then((r) => {
        if (!alive) return;
        const d = r.data?.data || r.data;
        setLoan(d);
        setForm((f) => ({ ...f, amount: f.amount || (d?.installment_amount ?? '') }));
      })
      .catch((e) => { if (alive) setErr(e.response?.data?.message || 'Could not load loan'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [loanId]);

  useEffect(load, [load]);

  const recordRepayment = () => {
    if (!num(form.amount)) { setErr('Enter a repayment amount'); return; }
    setBusy(true);
    setErr('');
    api.post(`/hr/loans/${loanId}/transactions`, {
      txn_type: 'repayment',
      amount: num(form.amount),
      txn_date: form.txn_date || today(),
      note: form.note || '',
    })
      .then(() => {
        setForm({ amount: '', txn_date: today(), note: '' });
        load();
        onChange?.();
      })
      .catch((e) => setErr(e.response?.data?.message || 'Could not record repayment'))
      .finally(() => setBusy(false));
  };

  const closeLoan = () => {
    setBusy(true);
    setErr('');
    api.patch(`/hr/loans/${loanId}`, { status: 'closed' })
      .then(() => { load(); onChange?.(); })
      .catch((e) => setErr(e.response?.data?.message || 'Could not close loan'))
      .finally(() => setBusy(false));
  };

  const txns = loan?.transactions || [];
  const outstanding = num(loan?.outstanding);

  return (
    <Modal title="Loan Ledger" width={680} onClose={onClose}>
      {loading ? <Loading /> : !loan ? <Empty>{err || 'Loan not found'}</Empty> : (
        <>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: HRC.text }}>{loan.full_name || '—'}</div>
              <div style={{ fontSize: 12, color: HRC.muted }}>{loan.employee_code || ''}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <TypeChip value={loan.loan_type} />
                <Pill value={loan.status} />
                <span style={{ fontSize: 12, color: HRC.muted }}>Principal {money(loan.principal)}</span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Outstanding</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: outstanding > 0 ? HRC.red : HRC.green }}>
                {outstanding > 0 ? money(outstanding) : '✓ Cleared'}
              </div>
              <div style={{ fontSize: 11, color: HRC.muted, marginTop: 2 }}>
                {money(loan.installment_amount)}/mo · {num(loan.installments_paid)}/{num(loan.installments_total)} paid
              </div>
            </div>
          </div>

          {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

          {/* Running-balance ledger */}
          <div style={{ fontSize: 12, color: HRC.muted, fontWeight: 700, marginBottom: 6 }}>
            Running Balance Ledger
          </div>
          {txns.length === 0 ? <Empty>No transactions yet</Empty> : (
            <Table>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Type</Th>
                  <Th style={{ textAlign: 'right' }}>Amount</Th>
                  <Th style={{ textAlign: 'right' }}>Running Balance</Th>
                  <Th>Note</Th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => {
                  const isDeb = t.txn_type === 'disbursement';
                  const sign = isDeb ? '+' : t.txn_type === 'repayment' ? '−' : '';
                  const c = txnColor(t.txn_type);
                  return (
                    <tr key={t.id}>
                      <Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.txn_date)}</Td>
                      <Td>
                        <span style={{
                          display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                          color: c, background: c + '1A', textTransform: 'capitalize', whiteSpace: 'nowrap',
                        }}>{String(t.txn_type || '').replace(/_/g, ' ')}</span>
                      </Td>
                      <Td style={{ textAlign: 'right', fontWeight: 700, color: c, whiteSpace: 'nowrap' }}>
                        {sign}{money(t.amount)}
                      </Td>
                      <Td style={{ textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{money(t.balance_after)}</Td>
                      <Td style={{ color: HRC.muted, maxWidth: 200, whiteSpace: 'normal' }}>{t.note || '—'}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          {/* Record repayment + close */}
          {loan.status !== 'closed' && loan.status !== 'cancelled' && (
            <div style={{ marginTop: 18, padding: 14, background: HRC.violet, borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: HRC.navy, fontWeight: 700, marginBottom: 10 }}>Record Repayment</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Amount">
                  <input type="number" min="0" step="0.01" value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder={String(loan.installment_amount || '')} style={input} />
                </Field>
                <Field label="Date">
                  <input type="date" max={today()} value={form.txn_date}
                    onChange={(e) => setForm({ ...form, txn_date: e.target.value })} style={input} />
                </Field>
              </div>
              <div style={{ marginTop: 12 }}>
                <Field label="Note (optional)">
                  <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="e.g. June salary deduction" style={input} />
                </Field>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
                <Btn variant="ghost" disabled={busy} onClick={closeLoan}>Close loan</Btn>
                <Btn variant="primary" disabled={busy} style={{ background: HRC.green }} onClick={recordRepayment}>
                  Record Repayment
                </Btn>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// ───────────────────────── New loan modal ─────────────────────────
function NewLoanModal({ onClose, onSaved }) {
  const [emps, setEmps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    employee_id: '', loan_type: 'loan', principal: '', installment_amount: '',
    installments_total: '', disbursed_date: today(), reason: '',
  });

  useEffect(() => {
    let alive = true;
    api.get('/hr/employees')
      .then((r) => { if (alive) setEmps(r.data?.data || r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = () => {
    if (!form.employee_id) { setErr('Select an employee'); return; }
    if (!num(form.principal)) { setErr('Enter a principal amount'); return; }
    setBusy(true);
    setErr('');
    api.post('/hr/loans', {
      employee_id: form.employee_id,
      loan_type: form.loan_type,
      principal: num(form.principal),
      installment_amount: num(form.installment_amount),
      installments_total: num(form.installments_total),
      disbursed_date: form.disbursed_date || today(),
      reason: form.reason || '',
    })
      .then(() => onSaved())
      .catch((e) => setErr(e.response?.data?.message || 'Could not create loan'))
      .finally(() => setBusy(false));
  };

  return (
    <Modal title="New Loan / Advance" width={560} onClose={onClose}>
      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Employee">
            <select value={form.employee_id} onChange={set('employee_id')} style={input}>
              <option value="">Select employee…</option>
              {emps.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.full_name}{e.employee_code ? ` (${e.employee_code})` : ''}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Type">
          <select value={form.loan_type} onChange={set('loan_type')} style={input}>
            <option value="loan">Loan</option>
            <option value="advance">Advance</option>
          </select>
        </Field>
        <Field label="Principal">
          <input type="number" min="0" step="0.01" value={form.principal} onChange={set('principal')}
            placeholder="0" style={input} />
        </Field>
        <Field label="Installment / month">
          <input type="number" min="0" step="0.01" value={form.installment_amount} onChange={set('installment_amount')}
            placeholder="0" style={input} />
        </Field>
        <Field label="No. of installments">
          <input type="number" min="0" step="1" value={form.installments_total} onChange={set('installments_total')}
            placeholder="0" style={input} />
        </Field>
        <Field label="Disbursed date">
          <input type="date" max={today()} value={form.disbursed_date} onChange={set('disbursed_date')} style={input} />
        </Field>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Reason / Note">
            <textarea rows={2} value={form.reason} onChange={set('reason')}
              placeholder="Purpose of the loan / advance" style={{ ...input, resize: 'vertical' }} />
          </Field>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={busy} onClick={save}>Create</Btn>
      </div>
    </Modal>
  );
}

// ───────────────────────── Main page ─────────────────────────
export default function LoansAdmin(props) {
  const { onChange } = props || {};
  const [typeKey, setTypeKey] = useState('all');
  const [statusKey, setStatusKey] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [ledgerId, setLedgerId] = useState(null);

  const typeVal = TYPE_TABS.find((t) => t.key === typeKey)?.value || null;
  const statusVal = STATUS_TABS.find((t) => t.key === statusKey)?.value || null;

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    const params = {};
    if (typeVal) params.type = typeVal;
    if (statusVal) params.status = statusVal;
    api.get('/hr/loans', { params })
      .then((r) => { if (alive) setRows(r.data?.data || r.data || []); })
      .catch((e) => {
        if (!alive) return;
        setErr(e.response?.data?.message || 'Could not load loans');
        setRows([]);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [typeVal, statusVal]);

  useEffect(load, [load]);

  const afterMutation = () => { load(); onChange?.(); };

  // KPI tiles — computed from the loaded list.
  const loans = rows.filter((r) => r.loan_type !== 'advance');
  const advances = rows.filter((r) => r.loan_type === 'advance');
  const activeLoans = loans.filter((r) => r.status === 'active');
  const activeAdvances = advances.filter((r) => r.status === 'active');
  const loanOutstanding = loans.reduce((s, r) => s + num(r.outstanding), 0);
  const advanceOutstanding = advances.reduce((s, r) => s + num(r.outstanding), 0);

  return (
    <Card>
      <SectionTitle right={<Btn variant="primary" onClick={() => setShowNew(true)}>+ New Loan / Advance</Btn>}>
        Loans &amp; Advances
      </SectionTitle>

      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        <Stat label="Active Loans" value={activeLoans.length} sub={`${loans.length} total`} accent={HRC.navy} />
        <Stat label="Loan Outstanding" value={money(loanOutstanding)} accent={HRC.red} />
        <Stat label="Active Advances" value={activeAdvances.length} sub={`${advances.length} total`} accent={HRC.amber} />
        <Stat label="Advance Outstanding" value={money(advanceOutstanding)} accent={HRC.amber} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Type</div>
          <SubTabs tabs={TYPE_TABS} active={typeKey} onChange={setTypeKey} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
          <SubTabs tabs={STATUS_TABS} active={statusKey} onChange={setStatusKey} />
        </div>
      </div>

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      <div style={{ fontSize: 12, color: HRC.muted, fontWeight: 600, marginBottom: 8 }}>
        {rows.length} record{rows.length === 1 ? '' : 's'}
      </div>

      {loading ? <Loading /> : rows.length === 0 ? (
        <Empty>No loans or advances found</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Employee</Th>
              <Th>Dept</Th>
              <Th>Type</Th>
              <Th style={{ textAlign: 'right' }}>Principal</Th>
              <Th style={{ textAlign: 'right' }}>Installment</Th>
              <Th>Progress</Th>
              <Th style={{ textAlign: 'right' }}>Outstanding</Th>
              <Th>Status</Th>
              <Th style={{ textAlign: 'right' }}>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const out = num(r.outstanding);
              return (
                <tr key={r.id}>
                  <Td>
                    <div style={{ fontWeight: 600 }}>{r.full_name || '—'}</div>
                    <div style={{ fontSize: 11, color: HRC.muted }}>{r.employee_code || ''}</div>
                  </Td>
                  <Td style={{ color: HRC.muted }}>{r.department_name || '—'}</Td>
                  <Td><TypeChip value={r.loan_type} /></Td>
                  <Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{money(r.principal)}</Td>
                  <Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {money(r.installment_amount)}<span style={{ color: HRC.muted, fontSize: 11 }}>/mo</span>
                  </Td>
                  <Td><Progress paid={r.installments_paid} total={r.installments_total} /></Td>
                  <Td style={{ textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', color: out > 0 ? HRC.red : HRC.green }}>
                    {out > 0 ? money(out) : '✓ Cleared'}
                  </Td>
                  <Td><Pill value={r.status} /></Td>
                  <Td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Btn size="sm" variant="ghost" onClick={() => setLedgerId(r.id)}>Ledger</Btn>
                      {r.status === 'active' && (
                        <Btn size="sm" variant="soft" onClick={() => setLedgerId(r.id)}>Record Repayment</Btn>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {showNew && (
        <NewLoanModal
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); afterMutation(); }}
        />
      )}
      {ledgerId != null && (
        <LedgerModal loanId={ledgerId} onClose={() => setLedgerId(null)} onChange={afterMutation} />
      )}
    </Card>
  );
}
